import type { ReactNode } from 'react';
import { juntarClasses } from './classes';
import { ROTULO_STATUS_CONTRATO, type StatusContrato, type TomSemantico } from './tipos';

/**
 * Selo de status.
 *
 * O texto fica sempre em `conteudo-primario` e a cor semântica vive no ponto e
 * na borda. Não é preferência estética: no tema claro, âmbar (3,07:1), verde
 * (3,6:1) e ciano (3,55:1) não alcançam os 4,5:1 exigidos para texto. Como
 * indicador não-textual, os mesmos tons passam com folga nos 3:1. Assim o selo é
 * legível nos dois temas hoje, sem esperar o ajuste de token registrado na
 * INTEGRACAO.md.
 *
 * E o ponto colorido nunca é o único sinal: o rótulo diz o estado por escrito —
 * daltonismo não pode custar a leitura de um contrato.
 */
interface EstiloDeTom {
  ponto: string;
  borda: string;
  fundo: string;
}

const TONS: Readonly<Record<TomSemantico, EstiloDeTom>> = {
  neutro: { ponto: 'bg-conteudo-sutil', borda: 'border-borda', fundo: 'bg-superficie-vidro/60' },
  informativo: {
    ponto: 'bg-acento-indigo',
    borda: 'border-acento-indigo/40',
    fundo: 'bg-acento-indigo/10',
  },
  progresso: {
    ponto: 'bg-acento-ciano',
    borda: 'border-acento-ciano/40',
    fundo: 'bg-acento-ciano/10',
  },
  atencao: { ponto: 'bg-atencao', borda: 'border-atencao/40', fundo: 'bg-atencao/10' },
  positivo: { ponto: 'bg-economia', borda: 'border-economia/40', fundo: 'bg-economia/10' },
  critico: { ponto: 'bg-erro', borda: 'border-erro/40', fundo: 'bg-erro/10' },
};

export type TamanhoSelo = 'pequeno' | 'medio';

export interface PropsSelo {
  children: ReactNode;
  tom?: TomSemantico;
  tamanho?: TamanhoSelo;
  /** Esconde o ponto — para selo de contagem ou etiqueta neutra. */
  semPonto?: boolean;
  /** Texto extra só para leitor de tela ("status do contrato: ..."). */
  contexto?: string;
  className?: string;
}

export function Selo({
  children,
  tom = 'neutro',
  tamanho = 'medio',
  semPonto = false,
  contexto,
  className,
}: PropsSelo): JSX.Element {
  const estilo = TONS[tom];
  return (
    <span
      className={juntarClasses(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border font-medium text-conteudo-primario',
        tamanho === 'pequeno' ? 'px-2 py-0.5 text-[0.6875rem]' : 'px-2.5 py-1 text-xs',
        estilo.borda,
        estilo.fundo,
        className,
      )}
    >
      {!semPonto && (
        <span
          aria-hidden="true"
          className={juntarClasses('h-1.5 w-1.5 shrink-0 rounded-full', estilo.ponto)}
        />
      )}
      {contexto !== undefined && <span className="sr-only">{contexto}: </span>}
      {children}
    </span>
  );
}

/**
 * Tom de cada estado do contrato (RN-018 a RN-030).
 *
 * A progressão conta a história do fluxo: neutro (em edição) → índigo (decisão
 * interna) → âmbar (parado numa decisão de terceiro, o gargalo histórico) →
 * ciano (em trânsito na assinatura) → verde (fechado, economia realizada).
 */
const TOM_POR_STATUS: Readonly<Record<StatusContrato, TomSemantico>> = {
  ABERTO: 'neutro',
  APROVACAO_ASSESSOR: 'informativo',
  APROVACAO_FORNECEDOR: 'atencao',
  EM_ASSINATURA: 'progresso',
  FECHADO: 'positivo',
};

export interface PropsSeloStatusContrato {
  status: StatusContrato;
  tamanho?: TamanhoSelo;
  className?: string;
}

export function SeloStatusContrato({
  status,
  tamanho = 'medio',
  className,
}: PropsSeloStatusContrato): JSX.Element {
  return (
    <Selo
      tom={TOM_POR_STATUS[status]}
      tamanho={tamanho}
      contexto="Status do contrato"
      className={className}
    >
      {ROTULO_STATUS_CONTRATO[status]}
    </Selo>
  );
}
